/**
 * Route Service
 *
 * API service for route planning and management.
 * Connects to FastAPI /api/routes endpoints.
 */

import { apiClient, handleApiError } from '@shared/services/apiClient';
import { apiCall } from '@shared/utils/apiWrapper';
import { logger } from '@shared/utils/logger';
import type {
  RouteListResponse,
  RouteDetail,
  RouteCalculationRequest,
  RouteCalculationResponse,
  SuccessResponse,
  RouteParams,
  RouteData,
  RouteHistory,
  RouteStopEnhanced,
  RoutePreferences,
  RouteStatistics,
  WeekRouteData,
  DayRouteData,
} from '../../../types/api';

/**
 * Backend API response format for weeks (nested Dict structure)
 * Different from frontend's WeekRouteData[] format
 */
interface BackendDayRoute {
  orgs: Array<{
    organization_id: number;
    organization_name: string;
    address: string;
    latitude: number;
    longitude: number;
    stop_number: number;
    distance_from_previous?: number;
    time_from_previous?: number;
    week?: number;
    day?: number;
  }>;
  total_drive_time: number; // Round-trip total
  total_distance: number; // Round-trip total
  route_source?: 'google_maps' | 'haversine' | 'osrm';
  encoded_polyline?: string;
  optimization_metrics?: {
    original_distance: number;
    optimized_distance: number;
    improvement_miles: number;
    improvement_percent: number;
  };
  // Round-trip breakdown fields
  start_to_first_distance?: number;
  start_to_first_time?: number;
  last_to_end_distance?: number;
  last_to_end_time?: number;
  one_way_distance?: number;
  one_way_time?: number;
}

interface BackendRouteResponse {
  campaign_id: string;
  route_id: string;
  route_name: string;
  total_stops: number;
  total_weeks: number;
  total_days: number;
  total_distance_miles: number;
  total_time_minutes: number;
  weeks: Record<string, Record<string, BackendDayRoute>>;
  created_date: string;
  // Start/end locations - can be at top level or nested in parameters
  start_location?: string;
  end_location?: string;
  parameters?: {
    start_location?: string;
    end_location?: string;
    starting_location?: string;  // Alias used by export_utils
    ending_location?: string;    // Alias used by export_utils
    // Geocoded coordinates for start/end locations (added via backfill)
    start_latitude?: number;
    start_longitude?: number;
    end_latitude?: number;
    end_longitude?: number;
    weeks?: number;
    days_per_week?: number;
    clustering_enabled?: boolean;
    distance_filter?: number | null;
    use_google_maps?: boolean;
    optimization_enabled?: boolean;
  };
}

/**
 * Transform backend's nested Dict weeks format to frontend's Array format
 *
 * Backend: { "1": { "1": {orgs: [...], total_drive_time: 60}, "2": {...} } }
 * Frontend: [{ week_number: 1, days: [{ day_number: 1, stops: [...] }] }]
 */
function transformWeeksToArray(
  backendWeeks: Record<string, Record<string, BackendDayRoute>>
): WeekRouteData[] {
  if (!backendWeeks || typeof backendWeeks !== 'object') {
    logger.log('[RouteAPI] transformWeeksToArray: No backend weeks data');
    return [];
  }

  // Debug: Log sample of backend data structure
  const weekKeys = Object.keys(backendWeeks);
  if (weekKeys.length > 0) {
    const firstWeekKey = weekKeys[0];
    const firstWeek = backendWeeks[firstWeekKey];
    const dayKeys = Object.keys(firstWeek || {});
    if (dayKeys.length > 0) {
      const firstDay = firstWeek[dayKeys[0]];
      const firstOrg = firstDay?.orgs?.[0];
      logger.log('[RouteAPI] Sample backend org data:', {
        hasOrgs: !!firstDay?.orgs,
        orgCount: firstDay?.orgs?.length,
        firstOrg: firstOrg ? {
          organization_id: firstOrg.organization_id,
          latitude: firstOrg.latitude,
          longitude: firstOrg.longitude,
          hasLatitude: firstOrg.latitude !== undefined,
          hasLongitude: firstOrg.longitude !== undefined,
        } : 'N/A',
        dayData: {
          start_to_first_time: firstDay?.start_to_first_time,
          last_to_end_time: firstDay?.last_to_end_time,
        },
      });
    }
  }

  const weekNumbers = Object.keys(backendWeeks)
    .map(Number)
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b);

  return weekNumbers.map((weekNum) => {
    const weekData = backendWeeks[String(weekNum)];
    const dayNumbers = Object.keys(weekData)
      .map(Number)
      .filter((n) => !isNaN(n))
      .sort((a, b) => a - b);

    const days: DayRouteData[] = dayNumbers.map((dayNum) => {
      const dayData = weekData[String(dayNum)];
      const stops: RouteStopEnhanced[] = (dayData.orgs || []).map((org, idx) => ({
        stop_id: `${org.organization_id}-${weekNum}-${dayNum}`,
        stop_number: idx + 1,
        organization_id: org.organization_id,
        organization_name: org.organization_name,
        address: org.address,
        verified_address: org.address,
        // Include both legacy (latitude/longitude) and new format (coordinates)
        latitude: org.latitude,
        longitude: org.longitude,
        coordinates: {
          lat: org.latitude,
          lng: org.longitude,
        },
        drive_time_minutes: org.time_from_previous,
        drive_time_from_previous: org.time_from_previous
          ? `${org.time_from_previous} min`
          : undefined,
        distance_from_previous_miles: org.distance_from_previous,
      }));

      const dayRoute: DayRouteData = {
        day_number: dayNum,
        stops,
        total_stops: stops.length,
        total_drive_time_minutes: dayData.total_drive_time || 0,
        total_distance_miles: dayData.total_distance || 0,
      };

      // Add optional route source and encoded polyline if available
      if (dayData.route_source) {
        dayRoute.route_source = dayData.route_source;
      }
      if (dayData.encoded_polyline) {
        dayRoute.encoded_polyline = dayData.encoded_polyline;
      }

      // Add round-trip breakdown fields if available
      if (dayData.start_to_first_distance !== undefined) {
        dayRoute.start_to_first_distance = dayData.start_to_first_distance;
      }
      if (dayData.start_to_first_time !== undefined) {
        dayRoute.start_to_first_time = dayData.start_to_first_time;
      }
      if (dayData.last_to_end_distance !== undefined) {
        dayRoute.last_to_end_distance = dayData.last_to_end_distance;
      }
      if (dayData.last_to_end_time !== undefined) {
        dayRoute.last_to_end_time = dayData.last_to_end_time;
      }
      if (dayData.one_way_distance !== undefined) {
        dayRoute.one_way_distance = dayData.one_way_distance;
      }
      if (dayData.one_way_time !== undefined) {
        dayRoute.one_way_time = dayData.one_way_time;
      }

      return dayRoute;
    });

    // Calculate week totals
    const totalStops = days.reduce((sum, d) => sum + d.total_stops, 0);
    const totalDriveTime = days.reduce((sum, d) => sum + d.total_drive_time_minutes, 0);
    const totalDistance = days.reduce((sum, d) => sum + d.total_distance_miles, 0);

    return {
      week_number: weekNum,
      days,
      total_stops: totalStops,
      total_drive_time_minutes: totalDriveTime,
      total_distance_miles: totalDistance,
    };
  });
}

/**
 * Transform backend route response to frontend RouteData format
 */
function transformRouteResponse(backendResponse: BackendRouteResponse): RouteData {
  const weeks = transformWeeksToArray(backendResponse.weeks);

  // Extract start/end locations from top level or nested parameters
  const startLocation = backendResponse.start_location
    || backendResponse.parameters?.start_location
    || backendResponse.parameters?.starting_location
    || '';
  const endLocation = backendResponse.end_location
    || backendResponse.parameters?.end_location
    || backendResponse.parameters?.ending_location
    || '';

  // DEBUG: Log parameters for coordinate debugging
  logger.log('[RouteAPI] transformRouteResponse - parameters:', backendResponse.parameters);

  return {
    route_id: backendResponse.route_id,
    campaign_id: backendResponse.campaign_id,
    created_at: backendResponse.created_date,
    weeks,
    total_weeks: backendResponse.total_weeks,
    total_stops: backendResponse.total_stops,
    total_drive_time_minutes: backendResponse.total_time_minutes,
    total_distance_miles: backendResponse.total_distance_miles,
    start_location: startLocation,
    end_location: endLocation,
    total_time_minutes: backendResponse.total_time_minutes,
    // CRITICAL: Pass parameters through for start/end coordinate extraction in routeTransformers.ts
    parameters: backendResponse.parameters,
  };
}

/**
 * Fetch list of routes
 *
 * @param campaignId - Optional campaign ID filter
 * @returns Promise resolving to route list
 *
 * @example
 * ```typescript
 * const routes = await fetchRoutes('CAMPAIGN_20251028_121851');
 * console.log(routes.total); // Total route count
 * ```
 */
export async function fetchRoutes(campaignId?: string): Promise<RouteListResponse> {
  const params = campaignId ? { campaign_id: campaignId } : {};
  return apiCall('fetch routes', () =>
    apiClient.get<RouteListResponse>('/campaigns/routes', { params })
  );
}

/**
 * Fetch single route by ID
 *
 * @param routeId - Route ID
 * @returns Promise resolving to route details
 *
 * @example
 * ```typescript
 * const route = await fetchRouteById('ROUTE_123456');
 * console.log(route.total_drive_time_minutes);
 * ```
 */
export async function fetchRouteById(routeId: string): Promise<RouteDetail> {
  return apiCall(`fetch route ${routeId}`, () =>
    apiClient.get<RouteDetail>(`/campaigns/${routeId}/route`)
  );
}

/**
 * Calculate routes for a campaign
 *
 * This triggers route optimization based on campaign configuration.
 *
 * @param data - Route calculation request
 * @returns Promise resolving to calculated routes
 *
 * @example
 * ```typescript
 * const result = await calculateRoutes({
 *   campaign_id: 'CAMPAIGN_20251028_121851',
 *   organization_ids: [123, 456, 789]  // Optional: calculate for specific orgs
 * });
 * console.log(`Generated ${result.total_routes} routes with ${result.total_stops} stops`);
 * ```
 */
export async function calculateRoutes(
  data: RouteCalculationRequest
): Promise<RouteCalculationResponse> {
  logger.log('[RouteAPI] Request data:', JSON.stringify(data, null, 2));

  const startTime = performance.now();
  try {
    // Use extended timeout (120s) for route calculation with many stops
    const result = await apiCall('calculate routes', () =>
      apiClient.post<RouteCalculationResponse>('/routes/calculate', data, {
        timeout: 300000, // 5 minutes for large route calculations (geocoding is slow)
      })
    );

    const apiTime = performance.now() - startTime;
    logger.log(`[RouteAPI] calculateRoutes completed in ${apiTime.toFixed(0)}ms`);
    logger.log('[RouteAPI] Response:', JSON.stringify(result, null, 2).substring(0, 500));
    return result;
  } catch (error) {
    const apiTime = performance.now() - startTime;
    logger.error(`[RouteAPI] calculateRoutes FAILED (${apiTime.toFixed(0)}ms):`, error);
    throw error;
  }
}

/**
 * Fetch route stops for a specific route
 *
 * @param routeId - Route ID
 * @returns Promise resolving to route stops list
 *
 * @example
 * ```typescript
 * const stops = await fetchRouteStops('ROUTE_123456');
 * stops.forEach(stop => {
 *   console.log(`Stop ${stop.stop_number}: ${stop.organization_name}`);
 * });
 * ```
 */
export async function fetchRouteStops(routeId: string): Promise<RouteStopEnhanced[]> {
  return apiCall(`fetch route stops for ${routeId}`, () =>
    apiClient.get<RouteStopEnhanced[]>(`/campaigns/${routeId}/stops`)
  );
}

/**
 * Delete route
 *
 * @param routeId - Route ID
 * @returns Promise resolving to success message
 *
 * @example
 * ```typescript
 * await deleteRoute('ROUTE_123456');
 * console.log('Route deleted successfully');
 * ```
 */
export async function deleteRoute(routeId: string): Promise<SuccessResponse> {
  return apiCall(`delete route ${routeId}`, () =>
    apiClient.delete<SuccessResponse>(`/campaigns/${routeId}`)
  );
}

/**
 * Export route to Excel or KML
 *
 * @param routeId - Route ID
 * @param format - Export format ('excel' or 'kml')
 * @returns Promise resolving to download URL
 *
 * @example
 * ```typescript
 * const url = await exportRoute('ROUTE_123456', 'excel');
 * window.open(url, '_blank'); // Download file
 * ```
 */
export async function exportRoute(
  routeId: string,
  format: 'excel' | 'kml' = 'excel'
): Promise<string> {
  const response = await apiCall(`export route ${routeId}`, () =>
    apiClient.post<{ download_url: string }>(
      `/campaigns/${routeId}/export`,
      { format }
    )
  );
  return response.download_url;
}

/**
 * Fetch route history
 *
 * @param campaignId - Optional campaign ID filter
 * @param startDate - Optional start date filter (YYYY-MM-DD)
 * @param endDate - Optional end date filter (YYYY-MM-DD)
 * @returns Promise resolving to route history
 *
 * @example
 * ```typescript
 * const history = await fetchRouteHistory('CAMPAIGN_20251028_121851', '2025-01-01', '2025-12-31');
 * console.log(history); // Historical route calculations
 * ```
 */
export async function fetchRouteHistory(
  campaignId?: string,
  startDate?: string,
  endDate?: string
): Promise<RouteHistory[]> {
  const params: Record<string, string> = {};
  if (campaignId) params.campaign_id = campaignId;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  try {
    return await apiCall('fetch route history', () =>
      apiClient.get<RouteHistory[]>('/routes/history', { params })
    );
  } catch (error: unknown) {
    // Handle 404 gracefully - endpoint may not exist on production or no history yet
    if (error instanceof Error && (error.message.includes('Not Found') || error.message.includes('404'))) {
      logger.log('[RouteAPI] No route history found (expected for new campaigns)');
      return [];
    }
    throw error;
  }
}

/**
 * Calculate multi-week route with clustering
 *
 * @param params - Route calculation parameters
 * @returns Promise resolving to calculated route data
 *
 * @example
 * ```typescript
 * const result = await calculateRoute({
 *   campaign_id: 'CAMPAIGN_20251028_121851',
 *   start_location: 'Houston, TX',
 *   end_location: 'Houston, TX',
 *   weeks: 12,
 *   days_per_week: 5,
 *   clustering_enabled: true,
 *   distance_filter: 50
 * });
 * console.log(`Generated ${result.total_stops} stops across ${result.total_weeks} weeks`);
 * ```
 */
export async function calculateRoute(params: RouteParams): Promise<RouteData> {
  logger.log('[RouteAPI] Request params:', JSON.stringify(params, null, 2));
  logger.log('[RouteAPI] Timeout: 300000ms (5 minutes)');

  // Backend returns Dict format, we need to transform to Array format
  // Use extended timeout (120s) for route calculation with many stops
  const startTime = performance.now();

  try {
    const backendResponse = await apiCall('calculate route', () =>
      apiClient.post<BackendRouteResponse>('/routes/calculate', params, {
        timeout: 300000, // 5 minutes for large route calculations (geocoding is slow)
      })
    );

    const apiTime = performance.now() - startTime;
    logger.log(`[RouteAPI] Backend response received in ${apiTime.toFixed(0)}ms`);
    logger.log('[RouteAPI] Raw backend response:', JSON.stringify(backendResponse, null, 2).substring(0, 1000) + '...');
    logger.log('[RouteAPI] Response keys:', Object.keys(backendResponse));
    logger.log('[RouteAPI] Weeks structure:', backendResponse.weeks ? Object.keys(backendResponse.weeks) : 'N/A');

    // Transform backend response to frontend format
    const transformedRoute = transformRouteResponse(backendResponse);

    logger.log('[RouteAPI] Transformed route:', {
      route_id: transformedRoute.route_id,
      campaign_id: transformedRoute.campaign_id,
      weeks_count: transformedRoute.weeks?.length || 0,
      total_stops: transformedRoute.total_stops,
      total_weeks: transformedRoute.total_weeks,
      total_distance_miles: transformedRoute.total_distance_miles,
      total_time_minutes: transformedRoute.total_time_minutes,
    });

    if (transformedRoute.weeks?.length > 0) {
      transformedRoute.weeks.forEach((week) => {
        logger.log(`[RouteAPI]   Week ${week.week_number}: ${week.days?.length || 0} days, ${week.total_stops || 0} stops, ${week.total_distance_miles?.toFixed(1) || 0} mi`);
      });
    }

    return transformedRoute;
  } catch (error) {
    const apiTime = performance.now() - startTime;
    logger.error(`[RouteAPI] ========== API CALL FAILED (${apiTime.toFixed(0)}ms) ==========`);
    logger.error('[RouteAPI] Error:', error);
    throw error;
  }
}

/**
 * Fetch full route data by route ID
 *
 * @param routeId - Route ID from history
 * @returns Promise resolving to full route data with all weeks/days/stops
 *
 * @example
 * ```typescript
 * const routeData = await fetchRouteData('ROUTE_20251120_123456');
 * console.log(routeData.weeks);
 * ```
 */
export async function fetchRouteData(routeId: string): Promise<RouteData> {

  const startTime = performance.now();

  try {
    const backendResponse = await apiCall(`fetch route ${routeId}`, () =>
      apiClient.get<BackendRouteResponse>(`/routes/${routeId}`)
    );

    const apiTime = performance.now() - startTime;
    logger.log(`[RouteAPI] Backend response received in ${apiTime.toFixed(0)}ms`);

    // Transform backend response to frontend format
    const transformedRoute = transformRouteResponse(backendResponse);

    logger.log('[RouteAPI] Loaded route:', {
      route_id: transformedRoute.route_id,
      campaign_id: transformedRoute.campaign_id,
      weeks_count: transformedRoute.weeks?.length || 0,
      total_stops: transformedRoute.total_stops,
    });

    return transformedRoute;
  } catch (error) {
    const apiTime = performance.now() - startTime;
    logger.error(`[RouteAPI] fetchRouteData FAILED (${apiTime.toFixed(0)}ms):`, error);
    throw error;
  }
}

/**
 * Save route preferences (Week 8 - POLISH-009)
 *
 * @param routeId - Route ID
 * @param preferences - Route preferences
 * @returns Promise resolving to success response
 *
 * @example
 * ```typescript
 * await saveRoutePreferences('ROUTE_20251120_001', {
 *   start_time: '09:00:00',
 *   end_time: '17:00:00',
 *   lunch_break_duration: 60,
 *   avg_stop_duration: 30,
 *   max_stops_per_day: 15,
 *   avg_mph: 30.0,
 *   fuel_mpg: 25.0,
 *   fuel_price_per_gallon: 3.50
 * });
 * ```
 */
export async function saveRoutePreferences(
  routeId: string,
  preferences: RoutePreferences
): Promise<SuccessResponse> {
  return apiCall(`save route preferences for ${routeId}`, () =>
    apiClient.post<SuccessResponse>(
      `/campaigns/${routeId}/preferences`,
      { preferences }
    )
  );
}

/**
 * Get route statistics (Week 8 - POLISH-009)
 *
 * @param routeId - Route ID
 * @param preferences - Optional custom preferences (uses saved if not provided)
 * @returns Promise resolving to route statistics
 *
 * @example
 * ```typescript
 * const stats = await getRouteStatistics('ROUTE_20251120_001');
 * console.log(`Total distance: ${stats.total_distance_miles} miles`);
 * console.log(`Fuel cost: $${stats.fuel_cost_estimate.toFixed(2)}`);
 * console.log(`Completion date: ${stats.estimated_completion_date}`);
 * ```
 */
export async function getRouteStatistics(
  routeId: string,
  preferences?: RoutePreferences
): Promise<RouteStatistics> {
  const params = preferences ? { preferences } : {};
  return apiCall(`get route statistics for ${routeId}`, () =>
    apiClient.get<RouteStatistics>(
      `/campaigns/${routeId}/stats`,
      { params }
    )
  );
}

/**
 * Delete a route from history
 *
 * @param routeId - Route ID to delete
 * @returns Promise resolving when deletion is complete
 *
 * @example
 * ```typescript
 * await deleteRouteFromHistory('ROUTE_20251120_001');
 * console.log('Route deleted successfully');
 * ```
 */
export async function deleteRouteFromHistory(routeId: string): Promise<void> {
  return apiCall(`delete route from history ${routeId}`, () =>
    apiClient.delete<void>(`/routes/history/${routeId}`)
  );
}

/**
 * Mark Visited Request Interface
 */
export interface MarkVisitedRequest {
  visited: boolean;
  visit_date?: string; // ISO YYYY-MM-DD
  outcome?: string;    // max 200 chars
  contact_met?: string; // max 100 chars
  notes?: string;      // max 1000 chars
}

/**
 * Mark Visited Response (ExecutionData)
 */
export interface ExecutionData {
  org_id: number;
  company_name: string;
  week?: number;
  day?: number;
  visited: boolean;
  visit_date?: string;
  outcome?: string;
  contact_met?: string;
  notes?: string;
  address?: string;
  selected_contact_name?: string;
  stop_order?: number;
}

/**
 * Mark organization as visited
 *
 * Updates visit status, date, outcome, and notes for the organization.
 *
 * @param campaignId - Campaign ID
 * @param orgId - Organization ID
 * @param request - Visit details
 * @returns Promise resolving to updated execution data
 *
 * @example
 * ```typescript
 * const result = await markOrganizationVisited('CAMPAIGN_123', 456, {
 *   visited: true,
 *   visit_date: '2025-12-01',
 *   outcome: 'Met contact, left materials',
 *   contact_met: 'John Doe',
 *   notes: 'Very receptive, interested in Q1 2025'
 * });
 * console.log('Organization marked as visited:', result.company_name);
 * ```
 */
export async function markOrganizationVisited(
  campaignId: string,
  orgId: number,
  request: MarkVisitedRequest
): Promise<ExecutionData> {
  return apiCall(`mark organization ${orgId} as visited`, () =>
    apiClient.put<ExecutionData>(
      `/campaigns/${campaignId}/execution/${orgId}/visited`,
      request
    )
  );
}

/**
 * Set active route for a campaign
 *
 * Persists the active route selection to PostgreSQL user_campaign_preferences table.
 * Uses typed columns (NOT JSONB) per CLAUDE.md PostgreSQL-First mandate.
 *
 * @param campaignId - Campaign ID
 * @param routeId - Route ID to set as active
 * @returns Promise resolving when the active route is set
 *
 * @example
 * ```typescript
 * await setActiveRoute('CAMPAIGN_123', 'ROUTE_20251201_001');
 * console.log('Active route set successfully');
 * ```
 */
export async function setActiveRoute(
  campaignId: string,
  routeId: string
): Promise<void> {
  await apiCall(`set active route ${routeId}`, () =>
    apiClient.put<void>(`/user-settings/campaign/${campaignId}/active-route`, {
      route_id: routeId,
    })
  );
}

/**
 * Get active route for a campaign
 *
 * Fetches from PostgreSQL user_campaign_preferences table.
 *
 * @param campaignId - Campaign ID
 * @returns Promise resolving to the active route ID or null if not set
 */
export async function getActiveRoute(campaignId: string): Promise<string | null> {
  try {
    const response = await apiClient.get<{ campaign_id: string; route_id: string | null }>(
      `/user-settings/campaign/${campaignId}/active-route`
    );
    const routeId = response.data.route_id || null;
    return routeId;
  } catch (error) {
    logger.error('[RouteAPI] Failed to get active route:', error);
    return null;
  }
}

/**
 * Update route name in history
 *
 * @param routeId - Route ID to update
 * @param routeName - New name for the route
 * @returns Promise resolving to the updated route history item
 *
 * @example
 * ```typescript
 * const updated = await updateRouteName('ROUTE_20251201_001', 'December Route');
 * console.log('Route renamed to:', updated.route_name);
 * ```
 */
export async function updateRouteName(
  routeId: string,
  routeName: string
): Promise<RouteHistory> {
  return apiCall(`update route name ${routeId}`, () =>
    apiClient.patch<RouteHistory>(`/routes/history/${routeId}/name`, {
      route_name: routeName,
    })
  );
}
